import { Controller } from "@hotwired/stimulus"
import { inAppShell } from "../lib/app_shell"
import { LayoutPreference } from "../lib/side_menu/layout_preference"
import { Peek } from "../lib/side_menu/peek"
import { BodyScrollLock } from "../lib/side_menu/scroll_lock"
import { clickIsOutsideMenu } from "../lib/side_menu/outside_click"
import { isMenuShortcut } from "../lib/side_menu/shortcut"
import { resetToClosed, slideIn, slideOut } from "../lib/side_menu/slide"

// Connects to data-controller="side-menu"
//
// Two layouts, chosen by the 768px breakpoint. On desktop the menu is always
// docked: there is no header up there, so the menu carries the search, inbox
// and account controls itself and the page content sits beside it, at full
// width or narrowed to an icon rail. Only in the docked shell, though: the
// classic layout (no `data-layout` stamp, see lib/app_shell.ts) keeps the
// header on desktop and this menu is its overlay there too. The rail is the one desktop preference,
// stored as `side-menu-rail` on <html> plus a cookie so Rails paints it on
// the first frame — see app/assets/stylesheets/components/side-menu.css,
// which owns everything visual. The rail peeks: hovering it widens the menu
// over the page without moving the page (lib/side_menu/peek.ts), and the rail
// toggle is what makes the page give way. On mobile the header stays and the menu is
// the same transient overlay it always was: the hamburger slides it over the
// page, and the next navigation, outside tap or Escape dismisses it.
//
// This class is the state machine; the DOM work lives in
// app/frontend/lib/side_menu/.
export default class extends Controller {
  static targets = ["menu", "railToggle"]
  static values = { editing: Boolean }

  declare readonly menuTarget: HTMLElement
  declare readonly hasMenuTarget: boolean
  declare readonly railToggleTargets: HTMLElement[]
  declare readonly editingValue: boolean

  private readonly rail = new LayoutPreference('side-menu-rail', 'side_menu_rail')
  private readonly scrollLock = new BodyScrollLock()
  private isMenuOpen = false
  private justToggled = false
  private wasMobile = false
  private readonly peek = new Peek()

  connect() {
    // The cookie covers Turbo visits arriving from a layout that never stamped
    // the class (kiosk, auth, public). Editing the menu needs every row's grip
    // and eye, so it renders at full width whatever the preference says; the
    // classic layout has no rail at all, and drops a class a docked page left
    // on <html>.
    if (this.editingValue || !inAppShell()) {
      this.rail.suspend()
    } else {
      this.rail.restore()
    }

    // Desktop is docked; the mobile overlay starts closed on every load.
    this.isMenuOpen = this.isDocked()
    this.wasMobile = this.isMobile()
    this.updateMenuVisibility(false)

    this.syncChrome()

    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener('click', this.handleOutsideClick)

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // Crossing 768px changes which stylesheet rules apply out from under us.
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize, { passive: true })

    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener('turbo:before-cache', this.handleBeforeCache)
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache)
    this.peek.detach()
    this.scrollLock.unlock()
  }

  // The mobile hamburger, and ⌘B everywhere. Docked, the menu is already on
  // screen, so the only thing left to toggle is its width.
  toggle() {
    if (this.isDocked()) {
      this.toggleRail()
      return
    }

    this.justToggled = true
    this.isMenuOpen = !this.isMenuOpen

    // Before the scroll lock changes, so the header does not react to the
    // resulting scroll event as a user scroll.
    this.dispatch(this.isMenuOpen ? 'menuOpened' : 'menuClosed', { bubbles: true })

    this.updateMenuVisibility(true)
    this.updateBodyScroll()

    // The hamburger's own click bubbles to document; this keeps it from
    // reading as an outside click and re-closing the menu.
    setTimeout(() => {
      this.justToggled = false
    }, 50)
  }

  // Called from every nav link, outside clicks, Escape, and the walkthrough.
  close() {
    // This single guard is what keeps the docked menu put: it neutralises the
    // `click->side-menu#close` on every nav link without touching any markup.
    // On mobile, close() is the only route to unlocking scroll.
    if (this.isDocked()) return
    if (!this.isMenuOpen) return

    this.isMenuOpen = false
    this.dispatch('menuClosed', { bubbles: true })
    this.updateMenuVisibility(true)
    this.updateBodyScroll()
  }

  open() {
    if (this.isMenuOpen) return

    this.isMenuOpen = true
    if (this.isMobile()) this.dispatch('menuOpened', { bubbles: true })
    this.updateMenuVisibility(true)
    this.updateBodyScroll()
  }

  // The collapse control at the top of the menu. Hidden below md, where the
  // rail stylesheet does not apply.
  toggleRail() {
    if (this.isMobile()) return
    // Edit mode owns the width; ⌘B and the hamburger reach here too.
    if (this.editingValue) return

    this.rail.set(!this.rail.value)
    this.syncChrome()
    this.keepFocusOnAToggle()
  }

  private isDocked(): boolean {
    return inAppShell() && !this.isMobile()
  }

  private isRailed(): boolean {
    return this.isDocked() && this.rail.value
  }

  private isMobile(): boolean {
    return window.innerWidth < 768
  }

  // Some layouts mount the controller without a menu.
  private get menu(): HTMLElement | null {
    return this.hasMenuTarget ? this.menuTarget : null
  }

  // Two toggles, one showing at a time: the panel icon at full width and the
  // logo in the rail. A keyboard user who just pressed the one that is now
  // hidden would otherwise lose focus to <body>.
  private keepFocusOnAToggle() {
    const active = document.activeElement as HTMLElement | null
    if (!active || !this.railToggleTargets.includes(active) || this.showing(active)) return

    this.railToggleTargets.find((toggle) => this.showing(toggle))?.focus()
  }

  // The panel icon leaves the rail with `visibility: hidden`, which keeps its
  // box (offsetParent) but not its focusability.
  private showing(element: HTMLElement): boolean {
    return element.checkVisibility?.({ visibilityProperty: true }) ?? element.offsetParent !== null
  }

  // Only the rail peeks; a peek left open by the state being switched away
  // from closes here. The toggles are named for the state they switch to.
  private syncChrome() {
    const railed = this.isRailed()
    this.railToggleTargets.forEach((toggle) => {
      const label = railed ? toggle.dataset.expandLabel : toggle.dataset.collapseLabel
      if (label) toggle.setAttribute('aria-label', label)
    })
    this.peek.attach(this.menu, railed)
  }

  private handleResize() {
    const mobile = this.isMobile()
    if (mobile === this.wasMobile) return
    this.wasMobile = mobile

    // Both the docked and rail stylesheets are gated on min-width: 768px, so a
    // breakpoint crossing changes whether the rail treatment applies at all.
    this.syncChrome()

    if (mobile) {
      // The docked rules have just stopped applying, so the menu would become
      // a full-screen overlay over a page that still scrolls. The menuClosed
      // dispatch matters too: smart-header's syncDesktopHeaderState bails
      // while it believes the menu is open, stranding the header in `fixed`
      // with body padding.
      if (this.isMenuOpen) {
        this.isMenuOpen = false
        this.dispatch('menuClosed', { bubbles: true })
        this.updateMenuVisibility(false)
      }
      this.scrollLock.unlock()
      return
    }

    // Leaving mobile with the overlay open: smart-header padded the body for a
    // header that is about to be display:none, so tell it the overlay closed.
    // The classic layout's overlay simply closes.
    if (this.isMenuOpen) this.dispatch('menuClosed', { bubbles: true })
    this.scrollLock.unlock()
    this.isMenuOpen = this.isDocked()
    this.updateMenuVisibility(false)
  }

  private handleBeforeCache() {
    const menu = this.menu
    if (!menu) return

    resetToClosed(menu, this.isDocked())
  }

  private updateMenuVisibility(animate: boolean) {
    const menu = this.menu
    if (!menu) return

    if (this.isMenuOpen) {
      slideIn(menu, animate)
    } else {
      slideOut(menu, animate, () => !this.isMenuOpen)
    }
  }

  private handleOutsideClick(event: Event) {
    const target = event.target as Element
    const menu = this.menu
    if (menu && !menu.contains(target)) this.peek.hide()

    if (!this.isMenuOpen || this.justToggled) return
    if (clickIsOutsideMenu(target, this.menu)) this.close()
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.peek.hide()
      this.close()
      return
    }

    // ⌘B is the hamburger.
    if (isMenuShortcut(event)) {
      event.preventDefault()
      this.toggle()
    }
  }

  private updateBodyScroll() {
    if (!this.isMobile()) return

    if (this.isMenuOpen) {
      this.scrollLock.lock()
    } else {
      this.scrollLock.unlock()
    }
  }
}
